import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { RuntimeCronJobRecordDto } from "@/lib/api/generated/control-api.schemas";
import {
  buildRuntimeCronRecordForSave,
  createRuntimeCronEditorState,
  type RuntimeCronJobRecord,
  type RuntimeCronEditorState,
} from "./runtime-cron-utils";
import { toastMutationError } from "@/lib/utils/formatting";

export function RuntimeCronJobEditorDialog({
  job,
  open,
  pending,
  onOpenChange,
  onSave,
}: {
  job: RuntimeCronJobRecordDto | null;
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (record: RuntimeCronJobRecord) => Promise<void>;
}) {
  const [state, setState] = useState<RuntimeCronEditorState | null>(null);

  useEffect(() => {
    if (!job || !open) {
      setState(null);
      return;
    }
    setState(createRuntimeCronEditorState(job));
  }, [job, open]);

  if (!job || !state) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-auto">
        <DialogHeader>
          <DialogTitle>{job.createdAt ? "Edit Runtime Cron Job" : "Create Runtime Cron Job"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input
                value={state.name}
                onChange={(event) => setState((prev) => prev ? { ...prev, name: event.target.value } : prev)}
              />
            </Field>
            <Field label="Description">
              <Input
                value={state.description}
                onChange={(event) =>
                  setState((prev) => prev ? { ...prev, description: event.target.value } : prev)
                }
              />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Enabled">
              <Switch
                checked={state.enabled}
                onCheckedChange={(checked) =>
                  setState((prev) => prev ? { ...prev, enabled: checked } : prev)
                }
              />
            </Field>
            <Field label="Delete After Run">
              <Switch
                checked={state.deleteAfterRun}
                onCheckedChange={(checked) =>
                  setState((prev) => prev ? { ...prev, deleteAfterRun: checked } : prev)
                }
              />
            </Field>
            <Field label="Session Target">
              <Select
                value={state.sessionTarget}
                onValueChange={(value) =>
                  setState((prev) => prev ? { ...prev, sessionTarget: value } : prev)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {state.sessionTarget.startsWith("session:") ? (
                    <SelectItem value={state.sessionTarget}>{state.sessionTarget}</SelectItem>
                  ) : null}
                  <SelectItem value="main">main</SelectItem>
                  <SelectItem value="isolated">isolated</SelectItem>
                  <SelectItem value="current">current</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Wake Mode">
              <Select
                value={state.wakeMode}
                onValueChange={(value) =>
                  setState((prev) => prev ? { ...prev, wakeMode: value } : prev)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="now">now</SelectItem>
                  <SelectItem value="next-heartbeat">next-heartbeat</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <SectionTitle>Schedule</SectionTitle>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Kind">
              <Select
                value={state.schedule.kind}
                onValueChange={(value) =>
                  setState((prev) => prev ? { ...prev, schedule: { ...prev.schedule, kind: value as RuntimeCronEditorState["schedule"]["kind"] } } : prev)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="at">at</SelectItem>
                  <SelectItem value="every">every</SelectItem>
                  <SelectItem value="cron">cron</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {state.schedule.kind === "at" ? (
              <Field label="At (ISO timestamp)">
                <Input
                  value={state.schedule.at}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, schedule: { ...prev.schedule, at: event.target.value } } : prev)
                  }
                  placeholder="2026-04-13T10:00:00Z"
                />
              </Field>
            ) : null}

            {state.schedule.kind === "every" ? (
              <>
                <Field label="Every (ms)">
                  <Input
                    type="number"
                    value={String(state.schedule.everyMs || "")}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, schedule: { ...prev.schedule, everyMs: Number(event.target.value) } } : prev)
                    }
                    placeholder="60000"
                  />
                </Field>
                <Field label="Anchor (ms, optional)">
                  <Input
                    type="number"
                    value={state.schedule.anchorMs === undefined ? "" : String(state.schedule.anchorMs)}
                    onChange={(event) =>
                      setState((prev) => {
                        if (!prev) return prev;
                        const trimmed = event.target.value.trim();
                        return {
                          ...prev,
                          schedule: {
                            ...prev.schedule,
                            anchorMs: trimmed.length === 0 ? undefined : Number(trimmed),
                          },
                        };
                      })
                    }
                    placeholder="0"
                  />
                </Field>
              </>
            ) : null}

            {state.schedule.kind === "cron" ? (
              <>
                <Field label="Expression">
                  <Input
                    value={state.schedule.expr}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, schedule: { ...prev.schedule, expr: event.target.value } } : prev)
                    }
                    placeholder="0 */5 * * * *"
                  />
                </Field>
                <Field label="Time Zone (optional)">
                  <Input
                    value={state.schedule.tz}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, schedule: { ...prev.schedule, tz: event.target.value } } : prev)
                    }
                    placeholder="UTC"
                  />
                </Field>
                <Field label="Stagger (ms, optional)">
                  <Input
                    type="number"
                    value={state.schedule.staggerMs === undefined ? "" : String(state.schedule.staggerMs)}
                    onChange={(event) =>
                      setState((prev) => {
                        if (!prev) return prev;
                        const trimmed = event.target.value.trim();
                        return {
                          ...prev,
                          schedule: {
                            ...prev.schedule,
                            staggerMs: trimmed.length === 0 ? undefined : Number(trimmed),
                          },
                        };
                      })
                    }
                    placeholder="0"
                  />
                </Field>
              </>
            ) : null}
          </div>

          <SectionTitle>Payload</SectionTitle>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Kind">
              <Select
                value={state.payload.kind}
                onValueChange={(value) =>
                  setState((prev) => prev ? { ...prev, payload: { ...prev.payload, kind: value as RuntimeCronEditorState["payload"]["kind"] } } : prev)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="systemEvent">systemEvent</SelectItem>
                  <SelectItem value="agentTurn">agentTurn</SelectItem>
                  <SelectItem value="workflowRun">workflowRun</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {state.payload.kind === "systemEvent" ? (
            <Field label="Text">
              <Textarea
                className="min-h-24"
                value={state.payload.text}
                onChange={(event) =>
                  setState((prev) => prev ? { ...prev, payload: { ...prev.payload, text: event.target.value } } : prev)
                }
              />
            </Field>
          ) : null}

          {state.payload.kind === "agentTurn" ? (
            <div className="grid gap-4">
              <Field label="Message">
                <Textarea
                  className="min-h-28"
                  value={state.payload.message}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, payload: { ...prev.payload, message: event.target.value } } : prev)
                  }
                />
              </Field>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Model (optional)">
                  <Input
                    value={state.payload.model}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, model: event.target.value } } : prev)
                    }
                  />
                </Field>
                <Field label="Fallbacks (CSV, optional)">
                  <Input
                    value={state.payload.fallbacksCsv}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, fallbacksCsv: event.target.value } } : prev)
                    }
                    placeholder="openai/gpt-4.1-mini, anthropic/claude-sonnet"
                  />
                </Field>
                <Field label="Thinking (optional)">
                  <Input
                    value={state.payload.thinking}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, thinking: event.target.value } } : prev)
                    }
                  />
                </Field>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <Field label="Timeout (s, optional)">
                  <Input
                    type="number"
                    value={state.payload.timeoutSeconds === undefined ? "" : String(state.payload.timeoutSeconds)}
                    onChange={(event) =>
                      setState((prev) => {
                        if (!prev) return prev;
                        const trimmed = event.target.value.trim();
                        return {
                          ...prev,
                          payload: {
                            ...prev.payload,
                            timeoutSeconds: trimmed.length === 0 ? undefined : Number(trimmed),
                          },
                        };
                      })
                    }
                  />
                </Field>
                <Field label="Allow Unsafe External Content">
                  <Switch
                    checked={state.payload.allowUnsafeExternalContent}
                    onCheckedChange={(checked) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, allowUnsafeExternalContent: checked } } : prev)
                    }
                  />
                </Field>
                <Field label="Light Context">
                  <Switch
                    checked={state.payload.lightContext}
                    onCheckedChange={(checked) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, lightContext: checked } } : prev)
                    }
                  />
                </Field>
                <Field label="Deliver">
                  <Switch
                    checked={state.payload.deliver}
                    onCheckedChange={(checked) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, deliver: checked } } : prev)
                    }
                  />
                </Field>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Channel (optional)">
                  <Input
                    value={state.payload.channel}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, channel: event.target.value } } : prev)
                    }
                  />
                </Field>
                <Field label="To (optional)">
                  <Input
                    value={state.payload.to}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, to: event.target.value } } : prev)
                    }
                  />
                </Field>
                <Field label="Best Effort Deliver">
                  <Switch
                    checked={state.payload.bestEffortDeliver}
                    onCheckedChange={(checked) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, bestEffortDeliver: checked } } : prev)
                    }
                  />
                </Field>
              </div>
            </div>
          ) : null}

          {state.payload.kind === "workflowRun" ? (
            <div className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Workflow Key">
                  <Input
                    value={state.payload.workflowKey}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, workflowKey: event.target.value } } : prev)
                    }
                    placeholder="workflow.automation.run"
                  />
                </Field>
                <Field label="Execution Class (optional)">
                  <Select
                    value={state.payload.executionClass}
                    onValueChange={(value) =>
                      setState((prev) => prev ? { ...prev, payload: { ...prev.payload, executionClass: value } } : prev)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="(default)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">(default)</SelectItem>
                      <SelectItem value="chat_only">chat_only</SelectItem>
                      <SelectItem value="approved_actions">approved_actions</SelectItem>
                      <SelectItem value="operator_maintenance">operator_maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Input (optional JSON object)">
                <Textarea
                  className="min-h-28 font-mono text-[11px]"
                  value={state.payload.inputJson}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, payload: { ...prev.payload, inputJson: event.target.value } } : prev)
                  }
                  placeholder={`{\n  \"example\": true\n}`}
                />
              </Field>
              <Field label="Input Summary (optional)">
                <Input
                  value={state.payload.inputSummary}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, payload: { ...prev.payload, inputSummary: event.target.value } } : prev)
                  }
                />
              </Field>
            </div>
          ) : null}

          <SectionTitle>Delivery</SectionTitle>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Enabled">
              <Switch
                checked={state.deliveryEnabled}
                onCheckedChange={(checked) =>
                  setState((prev) => prev ? { ...prev, deliveryEnabled: checked } : prev)
                }
              />
            </Field>
            {state.deliveryEnabled ? (
              <>
                <Field label="Mode">
                  <Select
                    value={state.delivery.mode}
                    onValueChange={(value) =>
                      setState((prev) => prev ? { ...prev, delivery: { ...prev.delivery, mode: value as RuntimeCronEditorState["delivery"]["mode"] } } : prev)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">none</SelectItem>
                      <SelectItem value="announce">announce</SelectItem>
                      <SelectItem value="webhook">webhook</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Channel (optional)">
                  <Input
                    value={state.delivery.channel}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, delivery: { ...prev.delivery, channel: event.target.value } } : prev)
                    }
                  />
                </Field>
                <Field label="To (optional)">
                  <Input
                    value={state.delivery.to}
                    onChange={(event) =>
                      setState((prev) => prev ? { ...prev, delivery: { ...prev.delivery, to: event.target.value } } : prev)
                    }
                  />
                </Field>
              </>
            ) : null}
          </div>

          {state.deliveryEnabled ? (
            <div className="grid gap-4 md:grid-cols-4">
              <Field label="Account ID (optional)">
                <Input
                  value={state.delivery.accountId}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, delivery: { ...prev.delivery, accountId: event.target.value } } : prev)
                  }
                />
              </Field>
              <Field label="Best Effort">
                <Switch
                  checked={state.delivery.bestEffort}
                  onCheckedChange={(checked) =>
                    setState((prev) => prev ? { ...prev, delivery: { ...prev.delivery, bestEffort: checked } } : prev)
                  }
                />
              </Field>
              <Field label="Failure Destination Enabled">
                <Switch
                  checked={state.delivery.failureDestinationEnabled}
                  onCheckedChange={(checked) =>
                    setState((prev) =>
                      prev
                        ? {
                            ...prev,
                            delivery: { ...prev.delivery, failureDestinationEnabled: checked },
                          }
                        : prev,
                    )
                  }
                />
              </Field>
            </div>
          ) : null}

          {state.deliveryEnabled && state.delivery.failureDestinationEnabled ? (
            <div className="grid gap-4 md:grid-cols-4">
              <Field label="Failure Dest Mode (optional)">
                <Select
                  value={state.delivery.failureDestination.mode}
                  onValueChange={(value) =>
                    setState((prev) =>
                      prev
                        ? {
                            ...prev,
                            delivery: {
                              ...prev.delivery,
                              failureDestination: { ...prev.delivery.failureDestination, mode: value as RuntimeCronEditorState["delivery"]["failureDestination"]["mode"] },
                            },
                          }
                        : prev,
                    )
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="(default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">(default)</SelectItem>
                    <SelectItem value="announce">announce</SelectItem>
                    <SelectItem value="webhook">webhook</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Failure Dest Channel (optional)">
                <Input
                  value={state.delivery.failureDestination.channel}
                  onChange={(event) =>
                    setState((prev) =>
                      prev
                        ? {
                            ...prev,
                            delivery: {
                              ...prev.delivery,
                              failureDestination: { ...prev.delivery.failureDestination, channel: event.target.value },
                            },
                          }
                        : prev,
                    )
                  }
                />
              </Field>
              <Field label="Failure Dest To (optional)">
                <Input
                  value={state.delivery.failureDestination.to}
                  onChange={(event) =>
                    setState((prev) =>
                      prev
                        ? {
                            ...prev,
                            delivery: {
                              ...prev.delivery,
                              failureDestination: { ...prev.delivery.failureDestination, to: event.target.value },
                            },
                          }
                        : prev,
                    )
                  }
                />
              </Field>
              <Field label="Failure Dest Account ID (optional)">
                <Input
                  value={state.delivery.failureDestination.accountId}
                  onChange={(event) =>
                    setState((prev) =>
                      prev
                        ? {
                            ...prev,
                            delivery: {
                              ...prev.delivery,
                              failureDestination: { ...prev.delivery.failureDestination, accountId: event.target.value },
                            },
                          }
                        : prev,
                    )
                  }
                />
              </Field>
            </div>
          ) : null}

          <SectionTitle>Failure Alert</SectionTitle>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Enabled">
              <Switch
                checked={state.failureAlertEnabled}
                onCheckedChange={(checked) =>
                  setState((prev) => prev ? { ...prev, failureAlertEnabled: checked } : prev)
                }
              />
            </Field>
            {state.failureAlertEnabled ? (
              <>
                <Field label="After (optional)">
                  <Input
                    type="number"
                    value={state.failureAlert.after === undefined ? "" : String(state.failureAlert.after)}
                    onChange={(event) =>
                      setState((prev) => {
                        if (!prev) return prev;
                        const trimmed = event.target.value.trim();
                        return {
                          ...prev,
                          failureAlert: {
                            ...prev.failureAlert,
                            after: trimmed.length === 0 ? undefined : Number(trimmed),
                          },
                        };
                      })
                    }
                  />
                </Field>
                <Field label="Mode (optional)">
                  <Select
                    value={state.failureAlert.mode}
                    onValueChange={(value) =>
                      setState((prev) => prev ? { ...prev, failureAlert: { ...prev.failureAlert, mode: value as RuntimeCronEditorState["failureAlert"]["mode"] } } : prev)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="(default)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">(default)</SelectItem>
                      <SelectItem value="announce">announce</SelectItem>
                      <SelectItem value="webhook">webhook</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Cooldown (ms, optional)">
                  <Input
                    type="number"
                    value={state.failureAlert.cooldownMs === undefined ? "" : String(state.failureAlert.cooldownMs)}
                    onChange={(event) =>
                      setState((prev) => {
                        if (!prev) return prev;
                        const trimmed = event.target.value.trim();
                        return {
                          ...prev,
                          failureAlert: {
                            ...prev.failureAlert,
                            cooldownMs: trimmed.length === 0 ? undefined : Number(trimmed),
                          },
                        };
                      })
                    }
                  />
                </Field>
              </>
            ) : null}
          </div>

          {state.failureAlertEnabled ? (
            <div className="grid gap-4 md:grid-cols-4">
              <Field label="Channel (optional)">
                <Input
                  value={state.failureAlert.channel}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, failureAlert: { ...prev.failureAlert, channel: event.target.value } } : prev)
                  }
                />
              </Field>
              <Field label="To (optional)">
                <Input
                  value={state.failureAlert.to}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, failureAlert: { ...prev.failureAlert, to: event.target.value } } : prev)
                  }
                />
              </Field>
              <Field label="Account ID (optional)">
                <Input
                  value={state.failureAlert.accountId}
                  onChange={(event) =>
                    setState((prev) => prev ? { ...prev, failureAlert: { ...prev.failureAlert, accountId: event.target.value } } : prev)
                  }
                />
              </Field>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={async () => {
              try {
                const record = buildRuntimeCronRecordForSave(job, state);
                await onSave(record);
                toast.success("Runtime cron job updated");
                onOpenChange(false);
              } catch (error) {
                toast.error(toastMutationError(error, "Failed to save runtime cron job"));
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="pt-2 text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
      {children}
    </div>
  );
}
